/*
  Nova Prime Blend — interactive video hero (inline on the PDP).

  State machine over pre-rendered clips:
    idle (loop) → tap → explode → zoomIn (auto) → benefits (loop, chips)
    → tap chip → reveal_<benefit> → focused (hover loop + info bubbles)

  Back walks the whole chain in reverse, one stage at a time:
    focused → reveal_<benefit>_reverse → benefits
    benefits → zoomOut (zoomIn reversed) → unexplode (explode reversed) → idle

  Every "resting" state is a seamless loop whose first/last frames are
  pixel-matched to the adjacent one-shot clips, so cuts are invisible.
  Tapping mid-loop fast-forwards to the seam instead of jump-cutting.
*/

(function () {
  'use strict';

  // One-shot transition clips (explode, zoomIn/Out, unexplode, whoosh both
  // ways, reviews both ways) have their 4x speed BAKED INTO THE FILES
  // (re-encoded 2026-09-07 with setpts=PTS/4 at 48fps) and play at 1x.
  // They used to be 5-6s renders played at playbackRate 4, which asks the
  // decoder for 96 frames a second of 1080p H.264; desktop and phone
  // decoders both dropped frames under that, which is the "starts
  // animating, then glitches" Alex saw. Same cure the benefit reveal clips
  // got earlier. Ambient loops stay at 1x either way.
  var TRANSITION_SPEED = 1;
  // prefers-reduced-motion: the hero becomes a still photo with instant
  // cuts — no loops, no transitions, no prefetch. Every destination state
  // (facts, reviews, benefits, focused) renders as its static end frame,
  // and all the same buttons work.
  var REDUCED = !!(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  // Benefit reveal/reverse clips have their speed BAKED INTO THE FILES
  // (~1.1s forward, ~0.8s back, re-rendered with ffmpeg setpts) and play at
  // 1x — high playbackRate values stutter on phones, baked speed cannot.

  // Browsers can reject play() (power saving, autoplay policy edge cases).
  // Never let that throw; the visibilitychange handler below resumes state.
  function safePlay(video) {
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  // Seamless-loop controller. The ORDINARY cycle uses the native `loop`
  // attribute: the browser wraps inside its media pipeline with no decoder
  // flush. It used to be done by hand (seek to 0 on every `ended`, and
  // from the rAF watch up to 80ms early), and every one of those seeks was
  // a visible hitch at the seam, roughly every five seconds while the hero
  // just sat there: the "small hiccups randomly" report. The hand-rolled
  // catch now runs ONLY once a tap has queued an advance, where landing
  // exactly on the seam frame matters: a rAF-based boundary watch — NOT
  // `timeupdate`, which the spec lets browsers throttle to ~250ms; at
  // fast-forward rates the seam window is narrow and timeupdate misses
  // it, leaving the video frozen at its natural end — plus the native
  // `ended` event as a hard safety net (native looping is switched off for
  // the queued cycle so that event can actually fire).
  function SeamlessLoop(video, onBoundary) {
    this.video = video;
    this.onBoundary = onBoundary;
    this.queued = false;
    this.rafId = null;
    var self = this;
    // A missing video (404'd asset, renamed key) must degrade to a dead
    // loop, not throw mid-init and take every button's wiring down with it.
    if (!video) return;
    video.addEventListener('ended', function () {
      self.handleBoundary();
    });
  }

  SeamlessLoop.prototype.start = function () {
    if (!this.video) return;
    this.queued = false;
    this.video.loop = true;
    this.video.currentTime = 0;
    this.video.playbackRate = 1;
    safePlay(this.video);
    this.watch();
  };

  SeamlessLoop.prototype.watch = function () {
    if (this.rafId) return;
    var self = this;
    var tick = function () {
      self.rafId = requestAnimationFrame(tick);
      self.check();
    };
    this.rafId = requestAnimationFrame(tick);
  };

  SeamlessLoop.prototype.stop = function () {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.queued = false;
  };

  SeamlessLoop.prototype.check = function () {
    var v = this.video;
    // Nothing to do on an ordinary cycle: the native loop wraps by itself.
    if (!this.queued || !v || v.paused || !v.duration) return;
    // Scale the catch window to playbackRate so one ~16ms rAF tick can
    // never jump past the seam, however fast we're fast-forwarding.
    var epsilon = Math.max(0.08, v.playbackRate * 0.05);
    if (v.duration - v.currentTime >= epsilon) return;
    this.handleBoundary();
  };

  SeamlessLoop.prototype.handleBoundary = function () {
    if (!this.video) return;
    if (this.queued) {
      this.stop();
      this.video.playbackRate = 1;
      this.onBoundary();
    } else {
      this.video.currentTime = 0;
      safePlay(this.video);
    }
  };

  // Tap-to-advance: ease playback up so the loop visibly "revs" to the seam
  // rather than jump-cutting or making the user wait for it.
  SeamlessLoop.prototype.queueAdvance = function () {
    if (this.queued || !this.video) return;
    this.queued = true;
    var v = this.video;
    // From here the seam is ours to catch: native looping off, so the
    // clip runs to its end and `ended` can fire as the safety net.
    v.loop = false;
    // The seam only arrives if the loop is advancing. Browsers pause muted
    // video without telling us (energy saving, Low Power Mode, tab switches)
    // and a paused loop would swallow the tap forever.
    safePlay(v);
    this.watch();
    var start = v.playbackRate;
    // Was 14: 336 decoded frames a second of a 24fps 1080p clip, which no
    // decoder delivers, so the rev itself stuttered right after the tap.
    // 4x is the most a decoder sustains cleanly; the loop is ~5s, so the
    // seam arrives within 1.3s, 0.6s on average.
    var target = 4;
    var rampMs = 100;
    var t0 = performance.now();
    var self = this;
    var step = function (now) {
      if (!self.queued) return;
      // rAF hands callbacks the frame's vsync timestamp, which can PRECEDE
      // t0 — unclamped, p goes negative, the computed rate goes negative,
      // and Chrome throws NotSupportedError, killing the ramp on tick one
      // (the tap then crawls to the seam at 1x).
      var p = Math.max(0, Math.min(1, (now - t0) / rampMs));
      v.playbackRate = start + (target - start) * p;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  var Nova3DHero = function () {
    return Reflect.construct(HTMLElement, [], Nova3DHero);
  };
  Nova3DHero.prototype = Object.create(HTMLElement.prototype);
  Nova3DHero.prototype.constructor = Nova3DHero;
  Object.setPrototypeOf(Nova3DHero, HTMLElement);

  Nova3DHero.prototype.connectedCallback = function () {
    if (this.initialized) return;
    this.initialized = true;
    var self = this;

    this.videos = {};
    this.querySelectorAll('[data-video]').forEach(function (v) {
      self.videos[v.dataset.video] = v;
    });
    this.hint = this.querySelector('[data-hint]');
    this.factsBtn = this.querySelector('[data-facts-btn]');
    this.factsOverlay = this.querySelector('[data-facts]');
    this.reviewsBtn = this.querySelector('[data-reviews-btn]');
    this.reviewsStill = this.querySelector('[data-reviews-still]');
    this.benefitsHeader = this.querySelector('[data-benefits-header]');
    this.chipsWrap = this.querySelector('[data-chips]');
    this.chips = this.querySelectorAll('[data-benefit]');
    this.panel = this.querySelector('[data-panel]');
    this.backBtn = this.querySelector('[data-back]');
    this.servingChip = this.querySelector('[data-serving]');
    this.allReviewsBtn = this.querySelector('[data-all-reviews]');
    this.benefits = JSON.parse(this.querySelector('[data-benefits]').textContent);
    this.warmed = {};
    this.state = 'idle';
    this.pendingBenefit = null;
    this.pendingBenefitAccent = '';
    this.factsQueued = false;
    this.reviewsQueued = false;

    // Hide the theme's standard gallery for this product. The element stays
    // in the DOM so theme scripts that query it keep working.
    var page = this.closest('.product__page');
    var gallery = page && page.querySelector('product-images');
    if (gallery) gallery.style.display = 'none';

    // The idle loop's seam serves three forward paths: the explosion, the
    // supplement-facts whoosh, or the reviews jar-launch. Each holds on the
    // seam frame until its clip is fully in memory — transitions play at 4x,
    // which drains a streaming buffer 4x faster than it refills, so playing
    // from the network mid-transition freezes; from a blob it cannot.
    this.idleLoop = new SeamlessLoop(this.videos.idle, function () {
      self.videos.idle.pause(); // hold the seam frame while (if) we wait
      if (self.factsQueued) {
        self.factsQueued = false;
        self.whenReady('whoosh', function () { self.startWhoosh(); });
      } else if (self.reviewsQueued) {
        self.reviewsQueued = false;
        self.whenReady('reviews', function () { self.startReviews(); });
      } else {
        self.whenReady('explode', function () { self.startExplode(); });
      }
    });
    // Powder/hover loops are amorphous textures, so transitions out of them
    // cut immediately (masked by the videos' CSS crossfade) instead of
    // fast-forwarding to the seam — taps must feel instant. Only the rigid
    // jar keeps seam-synchronized exits. These loops just loop.
    this.benefitsLoop = new SeamlessLoop(this.videos.benefitsLoop, function () {});
    this.hoverLoops = {};
    Object.keys(this.benefits).forEach(function (key) {
      self.hoverLoops[key] = new SeamlessLoop(
        self.videos['reveal_' + key + '_hover'],
        function () {}
      );
    });

    this.hintMessages = [
      { q: 'What does Nova Prime Blend do?', cta: 'Tap to find out \u2726', go: 'jar' },
      { q: "See what's actually inside", cta: 'Tap the jar \u2726', go: 'jar' },
      { q: '5.0 from verified buyers', cta: 'Read the reviews \u2605', go: 'reviews' },
      { q: 'Every dose on the label', cta: 'Supplement facts \u2726', go: 'facts' }
    ];
    this.hintIndex = 0;
    var hintQ = this.hint.querySelector('.nova3d__hint-question');
    var hintC = this.hint.querySelector('.nova3d__hint-cta');
    setInterval(function () {
      if (self.state !== 'idle' || self.hint.classList.contains('is-hidden')) return;
      self.hintIndex = (self.hintIndex + 1) % self.hintMessages.length;
      self.hint.classList.add('is-swapping');
      setTimeout(function () {
        var m = self.hintMessages[self.hintIndex];
        hintQ.textContent = m.q;
        hintC.textContent = m.cta;
        self.hint.classList.remove('is-swapping');
      }, 260);
    }, 4500);
    this.hint.addEventListener('click', function () {
      var go = self.hintMessages[self.hintIndex].go;
      if (go === 'reviews') self.tapReviews();
      else if (go === 'facts') self.tapFacts();
      else self.tapJar();
    });
    this.videos.idle.addEventListener('click', function () {
      self.tapJar();
    });
    this.videos.explode.addEventListener('ended', function () {
      self.onExplodeEnded();
    });
    this.videos.zoomIn.addEventListener('ended', function () {
      self.onZoomInEnded();
    });
    this.videos.zoomOut.addEventListener('ended', function () {
      self.onZoomOutEnded();
    });
    this.videos.unexplode.addEventListener('ended', function () {
      self.onUnexplodeEnded();
    });
    this.videos.whoosh.addEventListener('ended', function () {
      self.onWhooshEnded();
    });
    this.videos.whooshBack.addEventListener('ended', function () {
      self.onWhooshBackEnded();
    });
    this.factsBtn.addEventListener('click', function () {
      self.tapFacts();
    });
    this.videos.reviews.addEventListener('ended', function () {
      self.onReviewsEnded();
    });
    this.videos.reviewsBack.addEventListener('ended', function () {
      self.onReviewsBackEnded();
    });
    this.reviewsBtn.addEventListener('click', function () {
      self.tapReviews();
    });
    Object.keys(this.benefits).forEach(function (key) {
      if (!self.videos['reveal_' + key] || !self.videos['reveal_' + key + '_reverse']) return;
      self.videos['reveal_' + key].addEventListener('ended', function () {
        self.onRevealEnded(key);
      });
      self.videos['reveal_' + key + '_reverse'].addEventListener('ended', function () {
        self.onRevealReverseEnded(key);
      });
    });
    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        self.tapChip(chip);
      });
    });
    // One Back button, context-aware: from a focused benefit it returns to
    // the benefits menu; from the benefits menu it walks the powder back
    // into the jar. Each press = one stage backward, all the way home.
    if (this.allReviewsBtn) {
      this.allReviewsBtn.addEventListener('click', function () {
        self.track('all_reviews_click', {});
        // Ordered lookup: the review widget itself first (a combined
        // comma-selector returns the FIRST DOM match, which was a hidden
        // Loox svg store). Heading fallback must skip the hero's own
        // sr-only "What customers say" block.
        var target = document.getElementById('looxReviews') ||
          document.querySelector('.loox-widget');
        if (!target) {
          var h = Array.prototype.find.call(
            document.querySelectorAll('.shopify-section h2, .shopify-section h3'),
            function (el) {
              return /customers say|reviews/i.test(el.textContent) &&
                !el.closest('nova-3d-hero');
            }
          );
          target = h && h.closest('.shopify-section');
        }
        if (target) {
          target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
        }
      });
    }
    this.backBtn.addEventListener('click', function () {
      self.track('back', { hero_state: self.state });
      if (self.state === 'focused') self.backToBenefits();
      else if (self.state === 'benefits') self.backToJar();
      else if (self.state === 'facts') self.backFromFacts();
      else if (self.state === 'reviews') self.backFromReviews();
    });

    // Warm every primary path right after load, not on hover intent: the
    // web encodes total ~5MB for all first-hop clips, and hover-triggered
    // fetching plus a cold CDN edge (every fresh deploy) left real waits
    // between tap and motion. Data-saver / 2G connections keep the old
    // on-intent behavior.
    var conn = navigator.connection || {};
    var frugal = conn.saveData || /2g/.test(conn.effectiveType || '');
    var warmAll = function () {
      self.warmUp('explode');
      setTimeout(function () {
        self.warmUp('whoosh');
        self.warmUp('reviews');
      }, 800);
      setTimeout(function () {
        self.warmUp('zoomIn');
        self.warmUp('whooshBack');
        self.warmUp('reviewsBack');
      }, 1800);
      setTimeout(function () {
        self.warmUp('benefitsLoop');
      }, 2800);
    };
    if (REDUCED) {
      warmAll = function () {}; // clips never play; warm on demand per tap
    }
    if (frugal) {
      var warmOnIntent = function () {
        warmAll();
        self.removeEventListener('pointerenter', warmOnIntent);
        self.removeEventListener('touchstart', warmOnIntent);
      };
      this.addEventListener('pointerenter', warmOnIntent);
      this.addEventListener('touchstart', warmOnIntent, { passive: true });
    } else if (document.readyState === 'complete') {
      setTimeout(warmAll, 300);
    } else {
      window.addEventListener('load', function () { setTimeout(warmAll, 300); });
    }
    // Destination clips start warming on hover/first touch of their own
    // buttons, so the download has a head start before the tap lands.
    ['pointerenter', 'touchstart'].forEach(function (evt) {
      self.factsBtn.addEventListener(evt, function () {
        self.warmUp('whoosh');
      }, { passive: true });
      self.reviewsBtn.addEventListener(evt, function () {
        self.warmUp('reviews');
      }, { passive: true });
    });

    // A backgrounded tab can get its video paused by the browser. The rAF
    // watches idle harmlessly while paused; on return just resume whatever
    // the current state's video is.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      self.resumeIfPaused();
    });

    if (!REDUCED) this.idleLoop.start();
  };

  Nova3DHero.prototype.resumeIfPaused = function () {
    if (REDUCED) return;
    var b = this.pendingBenefit;
    var byState = {
      idle: this.videos.idle,
      queued: this.videos.idle,
      whooshQueued: this.videos.idle,
      whooshing: this.videos.whoosh,
      whooshingBack: this.videos.whooshBack,
      reviewsQueued: this.videos.idle,
      reviewsTransition: this.videos.reviews,
      reviewsBack: this.videos.reviewsBack,
      exploding: this.videos.explode,
      zooming: this.videos.zoomIn,
      benefits: this.videos.benefitsLoop,
      zoomingOut: this.videos.zoomOut,
      unexploding: this.videos.unexplode,
      panning: b && this.videos['reveal_' + b],
      focused: b && this.videos['reveal_' + b + '_hover'],
      panningBack: b && this.videos['reveal_' + b + '_reverse'],
    };
    var v = byState[this.state];
    if (v && v.paused && !v.ended) safePlay(v);
  };

  // Full prefetch, not streaming: browsers throttle how far ahead a paused
  // muted video buffers, and our transitions play at 4x — which burns the
  // buffer 4x faster than it refills and freezes the clip mid-move. The
  // files are ~0.3-1.6MB (web encodes), so fetch the whole thing into a
  // blob and play it locally: stalls become physically impossible.
  // Lightweight analytics: pushes to GTM's dataLayer (created if absent)
  // and gtag when present. Fails silent — tracking must never break UX.
  Nova3DHero.prototype.track = function (name, data) {
    try {
      var payload = Object.assign({ event: 'nova3d_' + name }, data || {});
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'nova3d_' + name, data || {});
      }
    } catch (e) { /* never let telemetry break the hero */ }
  };

  Nova3DHero.prototype.warmUp = function (key) {
    if (this.warmed[key]) return;
    this.warmed[key] = true;
    var self = this;
    var v = this.videos[key];
    fetch(v.currentSrc || v.src, { cache: 'force-cache' })
      .then(function (res) {
        if (!res.ok) throw new Error('http ' + res.status);
        return res.blob();
      })
      .then(function (blob) {
        self.adoptBlob(key, URL.createObjectURL(blob));
      })
      .catch(function () {
        // Network hiccup: fall back to letting the browser stream it, and
        // allow a retry on the next warmUp call.
        self.warmed[key] = false;
        var p = v.play();
        if (p && typeof p.then === 'function') {
          p.then(function () {
            v.pause();
            v.currentTime = 0;
          }).catch(function () {});
        }
      });
  };

  // Runs cb as soon as the clip is adopted as an in-memory blob (usually
  // already true, or within a few hundred ms of a tap thanks to intent
  // warming). Caps the wait at 3.5s and falls back to streaming so a
  // network failure degrades to the old behavior instead of a dead end.
  Nova3DHero.prototype.whenReady = function (key, cb) {
    var v = this.videos[key];
    if (v.src.indexOf('blob:') === 0) { cb(); return; }
    this.warmUp(key);
    var waited = 0;
    var poll = setInterval(function () {
      waited += 100;
      // Wait for the blob, not a short cap: falling back to streaming
      // starts a 4x playback that stalls mid-clip (the original disease).
      // 8s covers a cold CDN edge; past that something is truly wrong and
      // streaming is the least-bad option.
      if (v.src.indexOf('blob:') === 0 || waited >= 8000) {
        clearInterval(poll);
        cb();
      }
    }, 100);
  };

  // Swapping a video's src calls load(), which HARD-RESETS the element:
  // playback aborts, pending play() dies, playbackRate reverts. Doing that
  // to a clip that is mid-transition freezes the whole state machine (the
  // 'ended' event never fires). Only adopt the blob while the element is
  // safely idle — otherwise retry shortly; the streamed source carries the
  // current playback in the meantime.
  Nova3DHero.prototype.adoptBlob = function (key, url) {
    var self = this;
    var v = this.videos[key];
    var inUse = !v.paused || this.pendingSwapKey === key ||
      v.classList.contains('is-active') || v.classList.contains('is-prev');
    if (inUse) {
      setTimeout(function () { self.adoptBlob(key, url); }, 1500);
      return;
    }
    v.src = url;
    v.load();
  };

  // Layered swap, not a crossfade: the outgoing video holds its last frame
  // fully opaque UNDERNEATH while the incoming one fades in on top, then
  // gets hidden once covered. A true crossfade dips both layers to
  // semi-transparent mid-fade and flashes the dark stage through.
  //
  // Readiness gate: an un-decoded <video> paints BLACK, so if the incoming
  // clip has no frame yet (first uncached tap on a preload="none" asset),
  // the visual swap is deferred — the previous frame stays on screen — and
  // completes the moment the incoming video has data.
  Nova3DHero.prototype.showOnly = function (key) {
    var self = this;
    var incoming = this.videos[key];
    this.pendingSwapKey = key;
    clearTimeout(this.swapTimer);
    var doSwap = function () {
      if (self.pendingSwapKey !== key) return; // superseded by a newer swap
      Object.keys(self.videos).forEach(function (k) {
        var v = self.videos[k];
        if (v === incoming) return;
        if (v.classList.contains('is-active') || v.classList.contains('is-prev')) {
          v.classList.remove('is-active');
          v.classList.add('is-prev');
          if (!v.paused) v.pause();
        } else {
          v.classList.add('is-hidden');
          if (!v.paused) v.pause();
        }
      });
      incoming.classList.remove('is-hidden');
      incoming.classList.remove('is-prev');
      incoming.classList.add('is-active');
      // If the play() issued at transition start died while this swap was
      // deferred (e.g. the element got reset), revive it now. In reduced-
      // motion mode nothing should ever play — the paused first frame IS
      // the destination.
      if (!REDUCED && incoming.paused) safePlay(incoming);
      self.swapTimer = setTimeout(function () {
        Object.keys(self.videos).forEach(function (k) {
          var v = self.videos[k];
          if (v !== incoming) {
            v.classList.remove('is-prev');
            v.classList.add('is-hidden');
          }
        });
      }, 220);
    };
    if (incoming.readyState >= 2) {
      doSwap();
    } else {
      // Poll rather than wait for 'loadeddata': that event fires once per
      // load, so if the clip was already adopted and merely dipped to
      // readyState 1 from a currentTime seek, a one-shot listener would
      // never fire and the swap would hang forever (invisible transition,
      // frozen hero). readyState is always current.
      var waitReady = function () {
        if (self.pendingSwapKey !== key) return;
        if (incoming.readyState >= 2) doSwap();
        else requestAnimationFrame(waitReady);
      };
      requestAnimationFrame(waitReady);
    }
  };

  Nova3DHero.prototype.setBackVisible = function (visible) {
    this.backBtn.hidden = !visible;
  };

  /* ---------- Forward chain ---------- */

  Nova3DHero.prototype.tapJar = function () {
    if (this.state !== 'idle') return;
    this.track('jar_tap', {});
    this.hint.classList.add('is-hidden');
    this.factsBtn.classList.add('is-hidden');
    this.reviewsBtn.classList.add('is-hidden');
    if (REDUCED) {
      this.warmUp('benefitsLoop');
      this.enterBenefits(); // static: guards below keep every loop paused
      return;
    }
    this.state = 'queued';
    // Warm the whole forward chain, not just the first hop — the zoom
    // follows the explosion automatically and must also play from memory.
    this.warmUp('explode');
    this.warmUp('zoomIn');
    this.warmUp('benefitsLoop');
    this.idleLoop.queueAdvance();
  };

  /* ---------- Supplement facts ---------- */

  Nova3DHero.prototype.tapFacts = function () {
    if (this.state !== 'idle') return;
    this.hint.classList.add('is-hidden');
    this.factsBtn.classList.add('is-hidden');
    this.reviewsBtn.classList.add('is-hidden');
    if (REDUCED) {
      this.state = 'facts';
      if (this.factsOverlay) this.factsOverlay.classList.add('is-visible');
      this.setBackVisible(true);
      return;
    }
    this.state = 'whooshQueued';
    this.factsQueued = true;
    this.warmUp('whoosh');
    this.warmUp('whooshBack');
    this.idleLoop.queueAdvance();
  };

  /* ---------- Reviews ---------- */

  Nova3DHero.prototype.tapReviews = function () {
    if (this.state !== 'idle') return;
    this.hint.classList.add('is-hidden');
    this.factsBtn.classList.add('is-hidden');
    this.reviewsBtn.classList.add('is-hidden');
    if (REDUCED) {
      this.state = 'reviews';
      this.reviewsStill.classList.add('is-showing');
      this.setBackVisible(true);
      if (this.allReviewsBtn) this.allReviewsBtn.hidden = false;
      this.track('reviews_open', { reduced_motion: true });
      return;
    }
    this.state = 'reviewsQueued';
    this.reviewsQueued = true;
    this.warmUp('reviews');
    this.warmUp('reviewsBack');
    this.idleLoop.queueAdvance();
  };

  Nova3DHero.prototype.startReviews = function () {
    this.state = 'reviewsTransition';
    this.showOnly('reviews');
    this.videos.reviews.currentTime = 0;
    this.videos.reviews.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.reviews);
    this.warmUp('reviewsBack');
  };

  Nova3DHero.prototype.onReviewsEnded = function () {
    if (this.state !== 'reviewsTransition') return;
    // Seam-matched instant swap to the crisp wall still (the clip's end
    // frame is conditioned on this exact image) — a cut, not a fade.
    this.state = 'reviews';
    this.reviewsStill.classList.add('is-showing');
    this.setBackVisible(true);
    if (this.allReviewsBtn) this.allReviewsBtn.hidden = false;
    this.track('reviews_open', {});
  };

  Nova3DHero.prototype.backFromReviews = function () {
    if (this.state !== 'reviews') return;
    if (this.allReviewsBtn) this.allReviewsBtn.hidden = true;
    this.reviewsStill.classList.remove('is-showing');
    this.setBackVisible(false);
    if (REDUCED) {
      this.state = 'idle';
      this.showOnly('idle');
      this.hint.classList.remove('is-hidden');
      this.factsBtn.classList.remove('is-hidden');
      this.reviewsBtn.classList.remove('is-hidden');
      return;
    }
    this.state = 'reviewsBack';
    this.showOnly('reviewsBack');
    this.videos.reviewsBack.currentTime = 0;
    this.videos.reviewsBack.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.reviewsBack);
  };

  Nova3DHero.prototype.onReviewsBackEnded = function () {
    if (this.state !== 'reviewsBack') return;
    this.state = 'idle';
    this.showOnly('idle');
    this.hint.classList.remove('is-hidden');
    this.factsBtn.classList.remove('is-hidden');
    this.reviewsBtn.classList.remove('is-hidden');
    this.idleLoop.start();
  };

  Nova3DHero.prototype.startWhoosh = function () {
    this.state = 'whooshing';
    this.showOnly('whoosh');
    this.videos.whoosh.currentTime = 0;
    this.videos.whoosh.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.whoosh);
    this.warmUp('whooshBack');
  };

  Nova3DHero.prototype.onWhooshEnded = function () {
    if (this.state !== 'whooshing') return;
    // The whoosh's last frame (spinning motion blur) stays on screen while
    // the real, crisp supplement-facts panel fades in over it — the blur
    // "resolving into focus" on legible print.
    this.state = 'facts';
    if (this.factsOverlay) this.factsOverlay.classList.add('is-visible');
    this.setBackVisible(true);
    this.track('facts_open', {});
  };

  Nova3DHero.prototype.backFromFacts = function () {
    if (this.state !== 'facts') return;
    if (this.factsOverlay) this.factsOverlay.classList.remove('is-visible');
    this.setBackVisible(false);
    if (REDUCED) {
      this.state = 'idle';
      this.showOnly('idle');
      this.hint.classList.remove('is-hidden');
      this.factsBtn.classList.remove('is-hidden');
      this.reviewsBtn.classList.remove('is-hidden');
      return;
    }
    this.state = 'whooshingBack';
    this.showOnly('whooshBack');
    this.videos.whooshBack.currentTime = 0;
    this.videos.whooshBack.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.whooshBack);
  };

  Nova3DHero.prototype.onWhooshBackEnded = function () {
    if (this.state !== 'whooshingBack') return;
    this.state = 'idle';
    this.showOnly('idle');
    this.hint.classList.remove('is-hidden');
    this.factsBtn.classList.remove('is-hidden');
    this.reviewsBtn.classList.remove('is-hidden');
    this.idleLoop.start();
  };

  Nova3DHero.prototype.startExplode = function () {
    this.state = 'exploding';
    this.showOnly('explode');
    this.videos.explode.currentTime = 0;
    this.videos.explode.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.explode);
    this.warmUp('zoomIn');
  };

  Nova3DHero.prototype.onExplodeEnded = function () {
    if (this.state !== 'exploding') return;
    this.state = 'zooming';
    this.showOnly('zoomIn');
    this.videos.zoomIn.currentTime = 0;
    this.videos.zoomIn.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.zoomIn);
    this.warmUp('benefitsLoop');
  };

  Nova3DHero.prototype.enterBenefits = function () {
    this.state = 'benefits';
    this.showOnly('benefitsLoop');
    this.chipsWrap.hidden = false;
    this.benefitsHeader.hidden = false;
    if (this.servingChip) this.servingChip.hidden = false;
    this.setBackVisible(true);
    if (REDUCED) return; // static frame; loops and prefetch stay off
    var self = this;
    Object.keys(this.benefits).forEach(function (key) {
      self.warmUp('reveal_' + key);
    });
    this.warmUp('zoomOut');
    this.benefitsLoop.start();
  };

  Nova3DHero.prototype.onZoomInEnded = function () {
    if (this.state !== 'zooming') return;
    this.enterBenefits();
  };

  Nova3DHero.prototype.tapChip = function (chip) {
    if (this.state !== 'benefits') return;
    this.pendingBenefit = chip.dataset.benefit;
    this.pendingBenefitAccent = chip.style.getPropertyValue('--chip-accent').trim();
    this.chips.forEach(function (c) {
      c.classList.toggle('is-active', c === chip);
    });
    this.benefitsHeader.hidden = true;
    if (this.servingChip) this.servingChip.hidden = true;
    this.setBackVisible(false);
    this.benefitsLoop.stop();
    if (REDUCED) {
      this.warmUp('reveal_' + this.pendingBenefit + '_hover');
      this.chipsWrap.hidden = true;
      this.state = 'panning';
      this.onRevealEnded(this.pendingBenefit);
      return;
    }
    this.startReveal();
  };

  Nova3DHero.prototype.startReveal = function () {
    this.state = 'panning';
    this.chipsWrap.hidden = true;
    var video = this.videos['reveal_' + this.pendingBenefit];
    this.showOnly('reveal_' + this.pendingBenefit);
    video.currentTime = 0;
    video.playbackRate = 1; // speed baked into the file
    safePlay(video);
    this.warmUp('reveal_' + this.pendingBenefit + '_hover');
  };

  Nova3DHero.prototype.onRevealEnded = function (key) {
    if (this.state !== 'panning' || key !== this.pendingBenefit) return;
    this.state = 'focused';
    var data = this.benefits[key];
    var titleBubble = this.querySelector('[data-panel-title]');
    titleBubble.textContent = data.title;
    titleBubble.style.setProperty(
      '--benefit-accent',
      this.pendingBenefitAccent || 'rgba(255,255,255,0.18)'
    );
    this.querySelector('[data-panel-copy]').textContent = data.copy;

    // Formula: title top-left (below Back) → compact ingredient list
    // stacked directly beneath it → explanation bottom-center.
    var list = this.querySelector('[data-panel-ingredients]');
    list.textContent = '';
    data.ingredients.forEach(function (ing) {
      var bubble = document.createElement('div');
      bubble.className = 'nova3d__bubble nova3d__bubble--ingredient';
      var name = document.createElement('span');
      name.textContent = ing.name;
      var amount = document.createElement('span');
      amount.className = 'amount';
      amount.textContent = ing.amount;
      bubble.appendChild(name);
      bubble.appendChild(amount);
      list.appendChild(bubble);
    });
    this.panel.hidden = false;
    this.setBackVisible(true);
    this.track('benefit_open', { benefit: key });
    // Swap the finished one-shot reveal for its matching hover loop — same
    // pixel-matched-seam trick as everywhere else, so "just finished
    // forming" → "now gently drifting" is an invisible cut.
    this.showOnly('reveal_' + key + '_hover');
    if (REDUCED) return; // hold the hover clip's first frame, no drift
    this.hoverLoops[key].start();
    this.warmUp('reveal_' + key + '_reverse');
  };

  /* ---------- Backward chain ---------- */

  Nova3DHero.prototype.backToBenefits = function () {
    if (this.state !== 'focused') return;
    this.panel.hidden = true;
    this.setBackVisible(false);
    this.chips.forEach(function (c) {
      c.classList.remove('is-active');
    });
    this.hoverLoops[this.pendingBenefit].stop();
    if (REDUCED) {
      this.pendingBenefit = null;
      this.warmUp('benefitsLoop');
      this.enterBenefits();
      return;
    }
    this.startReverseFor(this.pendingBenefit);
  };

  Nova3DHero.prototype.startReverseFor = function (key) {
    if (key !== this.pendingBenefit) return;
    this.state = 'panningBack';
    var video = this.videos['reveal_' + key + '_reverse'];
    this.showOnly('reveal_' + key + '_reverse');
    video.currentTime = 0;
    video.playbackRate = 1; // speed baked into the file
    safePlay(video);
  };

  Nova3DHero.prototype.onRevealReverseEnded = function (key) {
    if (this.state !== 'panningBack' || key !== this.pendingBenefit) return;
    this.pendingBenefit = null;
    this.enterBenefits();
  };

  Nova3DHero.prototype.backToJar = function () {
    if (this.state !== 'benefits') return;
    this.benefitsHeader.hidden = true;
    if (this.servingChip) this.servingChip.hidden = true;
    this.setBackVisible(false);
    this.benefitsLoop.stop();
    if (REDUCED) {
      this.chipsWrap.hidden = true;
      this.state = 'idle';
      this.showOnly('idle');
      this.hint.classList.remove('is-hidden');
      this.factsBtn.classList.remove('is-hidden');
      this.reviewsBtn.classList.remove('is-hidden');
      return;
    }
    this.startZoomOut();
  };

  Nova3DHero.prototype.startZoomOut = function () {
    this.state = 'zoomingOut';
    this.chipsWrap.hidden = true;
    this.showOnly('zoomOut');
    this.videos.zoomOut.currentTime = 0;
    this.videos.zoomOut.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.zoomOut);
    this.warmUp('unexplode');
  };

  Nova3DHero.prototype.onZoomOutEnded = function () {
    if (this.state !== 'zoomingOut') return;
    this.state = 'unexploding';
    this.showOnly('unexplode');
    this.videos.unexplode.currentTime = 0;
    this.videos.unexplode.playbackRate = TRANSITION_SPEED;
    safePlay(this.videos.unexplode);
  };

  Nova3DHero.prototype.onUnexplodeEnded = function () {
    if (this.state !== 'unexploding') return;
    this.state = 'idle';
    this.showOnly('idle');
    this.hint.classList.remove('is-hidden');
    this.factsBtn.classList.remove('is-hidden');
    this.reviewsBtn.classList.remove('is-hidden');
    this.idleLoop.start();
  };

  if (!customElements.get('nova-3d-hero')) {
    customElements.define('nova-3d-hero', Nova3DHero);
  }
})();

// Arrow buttons for the self-contained carousel (snippets/nova-3d-modal.liquid)
// -- fully independent of Nova3DHero above, doesn't touch it or any
// Shopify gallery internals. Swiping already works via native
// scroll-snap with zero JS; this is just a click-friendly alternative
// that moves one slide at a time.
//
// The arrows loop: Next on the last slide goes back to the first, Previous
// on the first goes to the last, so the photos can be tapped through
// endlessly instead of having to be walked all the way back (Alex). Only the
// arrows do. A swipe still stops at the ends: native scroll-snap cannot wrap
// without cloned slides, and the first slide is the live video hero, which
// cannot be cloned.
//
// One binder, used here for the carousels on the page at load and by
// nova-chat-dock.js for the one it stamps into the chat later.
window.nova3dBindCarousel = function (carousel) {
  'use strict';
  var wrap = carousel.parentElement;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var step = function (direction) {
    var count = carousel.querySelectorAll('.nova3d-carousel__slide').length;
    var width = carousel.clientWidth;
    // The nearest slide, so a tap that lands mid-scroll counts from where
    // the carousel is heading, not from a half-way offset.
    var index = Math.round(carousel.scrollLeft / width);
    var target = (index + direction + count) % count;
    carousel.scrollTo({
      left: target * width,
      // The wrap crosses every slide; with reduced motion it jumps instead.
      // 'instant', not 'auto': the carousel's CSS sets scroll-behavior:
      // smooth, which 'auto' would defer to.
      behavior: reducedMotion.matches ? 'instant' : 'smooth'
    });
  };

  wrap.querySelector('[data-nova3d-carousel-prev]').addEventListener('click', function () { step(-1); });
  wrap.querySelector('[data-nova3d-carousel-next]').addEventListener('click', function () { step(1); });
};

Array.prototype.forEach.call(document.querySelectorAll('[data-nova3d-carousel]'), window.nova3dBindCarousel);
